import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  addDoc,
  collection,
  getDocs,
  query as firestoreQuery,
  serverTimestamp,
  where,
} from "firebase/firestore";
import "../App.css";
import db from "../firebase";
import NavBar from "../components/NavBar";

const API = "http://127.0.0.1:8000";
const RELATIONSHIP_DEFAULT_ROW = {
  personA: "",
  icon: "💙",
  personB: "",
  memo: "",
};
const RELATIONSHIP_ICONS = ["💙", "⚔️", "🤝", "🌀", "❓"];
const HOLIDAY_MAP = {
  "01-01": "신정",
  "03-01": "삼일절",
  "05-05": "어린이날",
  "06-06": "현충일",
  "08-15": "광복절",
  "10-03": "개천절",
  "10-09": "한글날",
  "12-25": "성탄절",
};

function stripHtmlTags(value = "") {
  return value.replace(/<[^>]+>/g, "");
}

function normalizeAuthor(author = "") {
  return author.split("^").join(", ");
}

function getCoverImageSrc(image = "") {
  return image.replace(/cover[^/]*\//, "cover200/");
}

function fallbackCoverImage(event) {
  const currentSrc = event.target.src;

  if (!currentSrc.includes("/cover200/")) {
    event.target.onerror = null;
    return;
  }

  event.target.src = currentSrc.replace("/cover200/", "/cover/");
  event.target.onerror = null;
}

function formatDate(dateStr) {
  if (!dateStr) return "";

  const clean = dateStr.replace(/-/g, "");
  if (clean.length !== 8) return dateStr;

  return `${clean.slice(0, 4)}.${clean.slice(4, 6)}.${clean.slice(6, 8)}`;
}

function toIsoDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function formatDisplayDate(dateString) {
  if (!dateString) return "";
  return dateString.replace(/-/g, ".");
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function getCalendarDays(viewDate) {
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDayIndex = new Date(year, month, 1).getDay();
  const lastDate = new Date(year, month + 1, 0).getDate();
  const cells = [];

  for (let index = 0; index < firstDayIndex; index += 1) {
    cells.push(null);
  }

  for (let day = 1; day <= lastDate; day += 1) {
    cells.push(new Date(year, month, day));
  }

  return cells;
}

function getHoliday(date) {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return HOLIDAY_MAP[`${month}-${day}`] || "";
}

function DatePicker({ value, onChange }) {
  const pickerRef = useRef(null);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const maxDate = toIsoDate(today);
  const selectedDate = value ? new Date(value) : today;
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => startOfMonth(selectedDate));

  useEffect(() => {
    if (!value) {
      onChange(maxDate);
    }
  }, [maxDate, onChange, value]);

  useEffect(() => {
    if (!value) return;
    setViewDate(startOfMonth(new Date(value)));
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!pickerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const maxMonth = startOfMonth(today);
  const minYear = Math.max(2020, today.getFullYear() - 6);
  const years = Array.from(
    { length: today.getFullYear() - minYear + 1 },
    (_, index) => minYear + index
  );
  const months = Array.from({ length: 12 }, (_, index) => index + 1).filter(
    (month) =>
      viewDate.getFullYear() < today.getFullYear() || month <= today.getMonth() + 1
  );
  const calendarDays = getCalendarDays(viewDate);

  const handleSelectChange = (type, rawValue) => {
    const nextYear = type === "year" ? Number(rawValue) : viewDate.getFullYear();
    const nextMonth =
      type === "month" ? Number(rawValue) - 1 : viewDate.getMonth();

    const nextDate = new Date(nextYear, nextMonth, 1);

    if (nextDate > maxMonth) {
      setViewDate(maxMonth);
      return;
    }

    setViewDate(nextDate);
  };

  return (
    <div className="date-picker" ref={pickerRef}>
      <button
        type="button"
        className={`field-date ${isOpen ? "open" : ""}`}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className="field-date-icon" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3.5" y="5" width="17" height="15" rx="3" />
            <path d="M7.5 3.5v3" />
            <path d="M16.5 3.5v3" />
            <path d="M3.5 9.5h17" />
            <path d="M8 13h.01" />
            <path d="M12 13h.01" />
            <path d="M16 13h.01" />
          </svg>
        </span>
        <span className="field-date-value">
          {formatDisplayDate(value || maxDate)}
        </span>
      </button>

      {isOpen && (
        <div className="date-picker-popover">
          <div className="date-picker-header">
            <button
              type="button"
              className="date-picker-tab"
              onClick={() => {
                onChange(maxDate);
                setViewDate(maxMonth);
              }}
            >
              오늘
            </button>

            <div className="date-picker-selects">
              <select
                className="date-picker-select"
                value={viewDate.getFullYear()}
                onChange={(event) => handleSelectChange("year", event.target.value)}
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}년
                  </option>
                ))}
              </select>
              <select
                className="date-picker-select"
                value={viewDate.getMonth() + 1}
                onChange={(event) => handleSelectChange("month", event.target.value)}
              >
                {months.map((month) => (
                  <option key={month} value={month}>
                    {month}월
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="date-picker-weekdays">
            {["일", "월", "화", "수", "목", "금", "토"].map((day) => (
              <span key={day}>{day}</span>
            ))}
          </div>

          <div className="date-picker-grid">
            {calendarDays.map((date, index) => {
              if (!date) {
                return <span key={`empty-${index}`} className="date-picker-empty" />;
              }

              const isoDate = toIsoDate(date);
              const isSelected = isoDate === (value || maxDate);
              const isFuture = isoDate > maxDate;
              const holidayName = getHoliday(date);
              const isWeekend = date.getDay() === 0 || date.getDay() === 6;
              const isHoliday = Boolean(holidayName);

              return (
                <button
                  key={isoDate}
                  type="button"
                  disabled={isFuture}
                  className={`date-picker-day${isSelected ? " selected" : ""}${
                    isWeekend || isHoliday ? " is-red" : ""
                  }`}
                  onClick={() => {
                    onChange(isoDate);
                    setIsOpen(false);
                  }}
                  title={holidayName || undefined}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function StarRating({ value, onChange }) {
  const [hover, setHover] = useState(0);

  return (
    <div className="star-rating">
      {[1, 2, 3, 4, 5].map((star) => (
        <span
          key={star}
          className={`star ${star <= (hover || value) ? "active" : ""}`}
          onClick={() => onChange(star)}
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
        >
          ★
        </span>
      ))}
    </div>
  );
}

function RelationshipInput({ value, onChange }) {
  const rows = value?.length ? value : [RELATIONSHIP_DEFAULT_ROW];

  const updateRow = (index, field, nextValue) => {
    const updatedRows = rows.map((row, rowIndex) =>
      rowIndex === index ? { ...row, [field]: nextValue } : row
    );
    onChange(updatedRows);
  };

  const addRow = () => {
    onChange([...rows, { ...RELATIONSHIP_DEFAULT_ROW }]);
  };

  const removeRow = (index) => {
    onChange(rows.filter((_, rowIndex) => rowIndex !== index));
  };

  return (
    <div className="relationship-input">
      {rows.map((row, index) => (
        <div
          key={`${row.personA}-${row.personB}-${index}`}
          className="relationship-row"
        >
          <input
            className="rel-input"
            placeholder="인물 A"
            value={row.personA}
            onChange={(event) => updateRow(index, "personA", event.target.value)}
          />
          <select
            className="rel-select"
            value={row.icon}
            aria-label="관계 선택"
            title="관계 선택"
            onChange={(event) => updateRow(index, "icon", event.target.value)}
          >
            {RELATIONSHIP_ICONS.map((icon) => (
              <option key={icon} value={icon}>
                {icon}
              </option>
            ))}
          </select>
          <input
            className="rel-input"
            placeholder="인물 B"
            value={row.personB}
            onChange={(event) => updateRow(index, "personB", event.target.value)}
          />
          <input
            className="rel-input rel-memo"
            placeholder="한줄 메모"
            value={row.memo}
            onChange={(event) => updateRow(index, "memo", event.target.value)}
          />
          <button
            type="button"
            className="rel-remove"
            onClick={() => removeRow(index)}
          >
            ✕
          </button>
        </div>
      ))}
      <button type="button" className="rel-add" onClick={addRow}>
        + 인물 추가
      </button>
    </div>
  );
}

function TemplateField({ field, value, onChange }) {
  switch (field.type) {
    case "text":
      return (
        <input
          className="field-input"
          type="text"
          placeholder={field.placeholder}
          value={value || ""}
          onChange={(event) => onChange(event.target.value)}
        />
      );
    case "date":
      return <DatePicker value={value} onChange={onChange} />;
    case "textarea":
      return (
        <textarea
          className="field-textarea"
          placeholder={field.placeholder}
          value={value || ""}
          onChange={(event) => onChange(event.target.value)}
        />
      );
    case "quote":
      return (
        <textarea
          className="field-quote"
          placeholder={field.placeholder}
          value={value || ""}
          onChange={(event) => onChange(event.target.value)}
        />
      );
    case "rating":
      return <StarRating value={value || 0} onChange={onChange} />;
    case "relationship":
      return <RelationshipInput value={value} onChange={onChange} />;
    default:
      return null;
  }
}

function HomePage() {
  const navigate = useNavigate();
  const debounceTimerRef = useRef(null);
  const toastTimerRef = useRef(null);
  const navigateTimerRef = useRef(null);

  const [query, setQuery] = useState("");
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showBookInfo, setShowBookInfo] = useState(false);
  const [showTemplate, setShowTemplate] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [formData, setFormData] = useState({});
  const [suggestions, setSuggestions] = useState([]);
  const [toast, setToast] = useState(null);
  const [bestsellers, setBestsellers] = useState([]);

  const getDraftKey = (currentBook) =>
    currentBook ? `yeobaek-draft:${currentBook.title}` : null;

  useEffect(() => {
    fetch(`${API}/bestsellers`)
      .then((response) => response.json())
      .then((data) => setBestsellers(data.items || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const debounceTimer = debounceTimerRef.current;
    const toastTimer = toastTimerRef.current;
    const navigateTimer = navigateTimerRef.current;

    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      if (toastTimer) {
        clearTimeout(toastTimer);
      }

      if (navigateTimer) {
        clearTimeout(navigateTimer);
      }
    };
  }, []);

  useEffect(() => {
    if (!showTemplate || !book) {
      return;
    }

    const draftKey = getDraftKey(book);
    if (!draftKey) {
      return;
    }

    try {
      const savedDraft = window.localStorage.getItem(draftKey);

      if (savedDraft) {
        setFormData(JSON.parse(savedDraft));
        setToast({ message: "임시저장된 기록을 불러왔어요.", type: "info" });
      } else {
        setFormData({});
      }
    } catch (draftError) {
      console.error(draftError);
    }
  }, [book, showTemplate]);

  useEffect(() => {
    if (!showTemplate || !book) {
      return;
    }

    const draftKey = getDraftKey(book);
    if (!draftKey) {
      return;
    }

    try {
      window.localStorage.setItem(draftKey, JSON.stringify(formData));
    } catch (draftError) {
      console.error(draftError);
    }
  }, [book, formData, showTemplate]);

  const showToast = (message, type = "success") => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }

    setToast({ message, type });

    if (type === "success") {
      return;
    }

    toastTimerRef.current = setTimeout(() => {
      setToast(null);
    }, 3000);
  };

  const clearDraft = (currentBook = book) => {
    const draftKey = getDraftKey(currentBook);

    if (!draftKey) {
      return;
    }

    try {
      window.localStorage.removeItem(draftKey);
    } catch (draftError) {
      console.error(draftError);
    }
  };

  const fetchSuggestions = async (value) => {
    const trimmedValue = value.trim();

    if (trimmedValue.length < 2) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await fetch(
        `${API}/book/suggestions?title=${encodeURIComponent(trimmedValue)}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch suggestions");
      }

      const data = await response.json();
      setSuggestions(Array.isArray(data.suggestions) ? data.suggestions : []);
    } catch (fetchError) {
      setSuggestions([]);
    }
  };

  const handleInputChange = (event) => {
    const nextValue = event.target.value;
    setQuery(nextValue);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchSuggestions(nextValue);
    }, 800);
  };

  const searchBook = async (overrideQuery, isbn) => {
    const searchQuery =
      typeof overrideQuery === "string" ? overrideQuery.trim() : query.trim();

    if (!searchQuery) {
      return;
    }

    setLoading(true);
    setError(null);
    setBook(null);
    setShowBookInfo(false);
    setShowTemplate(false);
    setShowDiscardConfirm(false);
    setFormData({});
    setSuggestions([]);

    try {
      const queryParams = new URLSearchParams({ title: searchQuery });

      if (isbn) {
        queryParams.set("isbn", isbn);
      }

      const response = await fetch(`${API}/book/search?${queryParams.toString()}`);

      if (!response.ok) {
        throw new Error("Failed to fetch book");
      }

      const data = await response.json();

      if (data.error) {
        setError("검색 결과가 없어요. 다른 제목으로 검색해보세요.");
        return;
      }

      setBook(data);

      const duplicateSnapshot = await getDocs(
        firestoreQuery(collection(db, "records"), where("title", "==", data.title))
      );

      if (!duplicateSnapshot.empty) {
        showToast("이미 서재에 있는 책이에요.", "info");
      }
    } catch (fetchError) {
      setError("서버 연결에 실패했어요.");
    } finally {
      setLoading(false);
    }
  };

  const updateField = (label, value) => {
    setFormData((prev) => ({ ...prev, [label]: value }));
  };

  const openTemplate = () => {
    setShowTemplate(true);
    setShowBookInfo(false);
  };

  const requestCloseTemplate = () => {
    setShowDiscardConfirm(true);
  };

  const discardDraft = () => {
    clearDraft();
    setFormData({});
    setShowDiscardConfirm(false);
    setShowTemplate(false);
  };

  const saveRecord = async () => {
    if (!book) {
      return;
    }

    try {
      await addDoc(collection(db, "records"), {
        title: book.title,
        author: book.author,
        publisher: book.publisher,
        pubdate: book.pubdate,
        image: book.image,
        genre: book.genre,
        record: formData,
        createdAt: serverTimestamp(),
      });

      clearDraft();
      setShowTemplate(false);
      showToast("독서기록이 저장됐어요.", "success");
    } catch (saveError) {
      showToast("저장에 실패했어요.", "error");
      console.error(saveError);
    }
  };

  const handleSuggestionSelect = (suggestion) => {
    const cleanTitle = stripHtmlTags(suggestion.title);
    setQuery(cleanTitle);
    setSuggestions([]);
    searchBook(cleanTitle, suggestion.isbn);
  };

  const templateFields = Array.isArray(book?.template) ? book.template : [];

  return (
    <div className="app">
      <NavBar />

      <div className="search-box">
        <div className="search-wrapper">
          <input
            className="search-input"
            autoFocus
            type="text"
            placeholder="책 제목을 입력하세요"
            value={query}
            onChange={handleInputChange}
            onFocus={() => {
              if (query.trim().length >= 2) {
                fetchSuggestions(query);
              }
            }}
            onBlur={() => setTimeout(() => setSuggestions([]), 150)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                searchBook(query);
              }
            }}
          />
          <button className="search-btn" onClick={() => searchBook()}>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </button>

          {suggestions.length > 0 && (
            <ul className="suggestions">
              {suggestions.map((suggestion) => (
                <li
                  key={suggestion.isbn || `${suggestion.title}-${suggestion.author}`}
                  className="suggestion-item"
                  onClick={() => handleSuggestionSelect(suggestion)}
                >
                  {suggestion.image && (
                    <img
                      src={getCoverImageSrc(suggestion.image)}
                      alt={stripHtmlTags(suggestion.title)}
                      className="suggestion-img"
                      onError={fallbackCoverImage}
                    />
                  )}
                  <div className="suggestion-info">
                    <span className="suggestion-title">
                      {stripHtmlTags(suggestion.title)}
                    </span>
                    <span className="suggestion-author">
                      {normalizeAuthor(suggestion.author)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {loading && <p className="loading">책 정보를 가져오는 중...</p>}
      {error && <p className="error">{error}</p>}

      {!book && !loading && bestsellers.length > 0 && (
        <div className="main-section">
          <p className="section-label">이번 주 베스트셀러</p>
          <div className="bestseller-row">
            {bestsellers.map((item) => (
              <div
                key={item.isbn || item.title}
                className="bestseller-item"
                onClick={() => {
                  const cleanTitle = stripHtmlTags(item.title);
                  setQuery(cleanTitle);
                  searchBook(cleanTitle, item.isbn);
                }}
              >
                {item.image ? (
                  <img
                    className="bestseller-img"
                    src={getCoverImageSrc(item.image)}
                    alt={stripHtmlTags(item.title)}
                    onError={fallbackCoverImage}
                  />
                ) : (
                  <div className="bestseller-noimage">
                    {stripHtmlTags(item.title)}
                  </div>
                )}
                <span className="bestseller-rank">{item.rank}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {book && !loading && (
        <>
          <div
            className="book-card"
            onClick={() => setShowBookInfo(true)}
            style={{ cursor: "pointer" }}
          >
            {book.image && (
              <img
                className="book-image"
                src={getCoverImageSrc(book.image)}
                alt={book.title}
                onError={fallbackCoverImage}
              />
            )}
            <div className="book-info">
              <div className="genre-tag">{book.genre}</div>
              <h2>{book.title}</h2>
              <p>{normalizeAuthor(book.author)}</p>
              <p>
                {book.publisher} · {formatDate(book.pubdate)}
              </p>
              <p className="book-desc">{book.description}</p>
            </div>
          </div>

          {!showTemplate && (
            <button className="start-btn" onClick={openTemplate}>
              독서 여백 채우기 →
            </button>
          )}

          {showBookInfo && (
            <div className="popup-overlay" onClick={() => setShowBookInfo(false)}>
              <div className="popup-card" onClick={(event) => event.stopPropagation()}>
                <div className="popup-header">
                  <span />
                  <button
                    type="button"
                    className="popup-close"
                    onClick={() => setShowBookInfo(false)}
                  >
                    ✕
                  </button>
                </div>
                <div className="popup-body">
                  <div className="popup-book-header">
                    {book.image && (
                      <img
                        src={getCoverImageSrc(book.image)}
                        alt={book.title}
                        className="popup-book-image"
                        onError={fallbackCoverImage}
                      />
                    )}
                    <div className="popup-book-meta">
                      <div className="genre-tag">{book.genre}</div>
                      <h2 className="popup-book-title">{book.title}</h2>
                      <p className="popup-book-author">{normalizeAuthor(book.author)}</p>
                      <p className="popup-book-pub">
                        {book.publisher} · {formatDate(book.pubdate)}
                      </p>
                    </div>
                  </div>
                  <div className="popup-book-body">
                    <p className="popup-book-desc">{book.description}</p>
                  </div>
                </div>
                {!showTemplate && (
                  <button className="start-btn" onClick={openTemplate}>
                    독서 여백 채우기 →
                  </button>
                )}
              </div>
            </div>
          )}

          {showTemplate && (
            <div className="popup-overlay" onClick={requestCloseTemplate}>
              <div
                className="popup-card popup-form"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="popup-header">
                  <h3 className="template-title">{book.title}</h3>
                  <button
                    type="button"
                    className="popup-close"
                    onClick={requestCloseTemplate}
                  >
                    ✕
                  </button>
                </div>
                <div className="popup-body">
                  {templateFields.map((field) => (
                    <div key={field.label} className="field-group">
                      <label className="field-label">{field.label}</label>
                      <TemplateField
                        field={field}
                        value={formData[field.label]}
                        onChange={(nextValue) => updateField(field.label, nextValue)}
                      />
                    </div>
                  ))}
                  <button className="save-btn" onClick={saveRecord}>
                    저장하기
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {showDiscardConfirm && (
        <div className="popup-overlay" onClick={() => setShowDiscardConfirm(false)}>
          <div
            className="confirm-popup"
            onClick={(event) => event.stopPropagation()}
          >
            <p className="confirm-popup-title">
              여백을 채우지 못했습니다.
              <br />
              기록을 중단하시겠습니까?
            </p>
            <p className="confirm-popup-desc">
              확인을 누르면 입력하던 내용과 임시저장이 모두 삭제됩니다.
            </p>
            <div className="confirm-popup-actions">
              <button
                type="button"
                className="confirm-popup-btn secondary"
                onClick={() => setShowDiscardConfirm(false)}
              >
                계속 작성
              </button>
              <button
                type="button"
                className="confirm-popup-btn danger"
                onClick={discardDraft}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {toast?.type === "success" && (
        <div className="popup-overlay" onClick={() => setToast(null)}>
          <div className="save-popup" onClick={(event) => event.stopPropagation()}>
            <p className="save-popup-emoji">🌿</p>
            <p className="save-popup-title">
              <span>{book?.title}</span>의 여백을 채웠어요
            </p>
            <div className="save-popup-buttons">
              <button
                className="save-popup-btn secondary"
                onClick={() => {
                  setToast(null);
                  setBook(null);
                  setQuery("");
                  setFormData({});
                }}
              >
                추가 등록하기
              </button>
              <button
                className="save-popup-btn primary"
                onClick={() => navigate("/library")}
              >
                서재로 이동
              </button>
            </div>
          </div>
        </div>
      )}

      {(toast?.type === "error" || toast?.type === "info") && (
        <div className={`toast ${toast.type}`}>{toast.message}</div>
      )}
    </div>
  );
}

export default HomePage;
